import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import RequestAccount from "./pages/RequestAccount";
import Dashboard from "./pages/Dashboard";
import Tickets from "./pages/Tickets";
import Tasks from "./pages/Tasks";
import SubTasks from "./pages/SubTasks";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import Timesheets from "./pages/Timesheets";
import CRM from "./pages/CRM";
import CrmCustomer from "./pages/CrmCustomer";
import Communications from "./pages/Communications";
import Approvals from "./pages/Approvals";
import Quality from "./pages/Quality";
import Reports from "./pages/Reports";
import Users from "./pages/Users";
import UserProfile from "./pages/UserProfile";
import UserActivity from "./pages/UserActivity";
import PerformancePolicyPage from "./pages/PerformancePolicy";
import MyPerformance from "./pages/MyPerformance";
import CommentPolicyPage from "./pages/CommentPolicy";
import Audit from "./pages/Audit";
import RolesPermissions from "./pages/RolesPermissions";
import WhmcsConfig from "./pages/WhmcsConfig";
import WhmcsTickets from "./pages/WhmcsTickets";
import WhmcsTicketDetail from "./pages/WhmcsTicketDetail";
import WhmcsQuality from "./pages/WhmcsQuality";
import AiConfig from "./pages/AiConfig";

import EmployeePoints from "./pages/EmployeePoints";
import MyRequests from "./pages/MyRequests";
import RequestApprovals from "./pages/RequestApprovals";
import LeaveBalances from "./pages/LeaveBalances";
import RequestsReport from "./pages/RequestsReport";
import ServiceRequests from "./pages/ServiceRequests";
import HR from "./pages/HR";
import EmployeeProfile from "./pages/EmployeeProfile";
import InstallPrompt from "./components/InstallPrompt";

/* Spinner shown while auth state is being restored from localStorage */
const Spinner = () => (
  <div className="flex items-center justify-center h-screen bg-gray-50">
    <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
  </div>
);

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();
  if (isLoading) return <Spinner />;          // ← wait for localStorage restore
  return user ? <>{children}</> : <Navigate to="/login" replace />;
};

const TabRoute: React.FC<{ tabKey: string; children: React.ReactNode }> = ({ tabKey, children }) => {
  const { isAdmin, canAccessTab } = useAuth();
  if (!canAccessTab(tabKey) && !isAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
};

const App: React.FC = () => (
  <AuthProvider>
    <Toaster position="top-right" />
    <InstallPrompt />
    <BrowserRouter>
      <Routes>
        <Route path="/login"           element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password"  element={<ResetPassword />} />
        <Route path="/request-account" element={<RequestAccount />} />
        <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
          <Route index                    element={<Dashboard />} />
          <Route path="tickets"           element={<TabRoute tabKey="tickets"><Tickets /></TabRoute>} />
          <Route path="tasks"             element={<TabRoute tabKey="tasks"><Tasks /></TabRoute>} />
          <Route path="subtasks"          element={<TabRoute tabKey="tasks"><SubTasks /></TabRoute>} />
          <Route path="projects"          element={<TabRoute tabKey="projects"><Projects /></TabRoute>} />
          <Route path="projects/:id"      element={<ProjectDetail />} />
          <Route path="timesheets"        element={<TabRoute tabKey="timesheets"><Timesheets /></TabRoute>} />
          <Route path="crm"               element={<TabRoute tabKey="crm"><CRM /></TabRoute>} />
          <Route path="crm/:id"           element={<CrmCustomer />} />
          <Route path="communications"    element={<TabRoute tabKey="communications"><Communications /></TabRoute>} />
          <Route path="approvals"         element={<TabRoute tabKey="approvals"><Approvals /></TabRoute>} />
          <Route path="quality"           element={<TabRoute tabKey="quality"><Quality /></TabRoute>} />
          <Route path="reports"           element={<TabRoute tabKey="reports"><Reports /></TabRoute>} />
          <Route path="users"             element={<TabRoute tabKey="users"><Users /></TabRoute>} />
          <Route path="users/:id"         element={<UserActivity />} />
          <Route path="audit"             element={<Audit />} />
          <Route path="roles-permissions" element={<RolesPermissions />} />
          <Route path="comment-policy"    element={<CommentPolicyPage />} />
          <Route path="performance-policy" element={<PerformancePolicyPage />} />
          <Route path="my-performance"    element={<MyPerformance />} />
          <Route path="whmcs-tickets"     element={<TabRoute tabKey="whmcs-tickets"><WhmcsTickets /></TabRoute>} />
          <Route path="whmcs-tickets/:id" element={<TabRoute tabKey="whmcs-tickets"><WhmcsTicketDetail /></TabRoute>} />
          <Route path="whmcs-quality"     element={<TabRoute tabKey="whmcs-tickets"><WhmcsQuality /></TabRoute>} />
          <Route path="ai-config"         element={<AiConfig />} />
          <Route path="whmcs-config"      element={<WhmcsConfig />} />
          <Route path="employee-points"   element={<EmployeePoints />} />
          <Route path="my-requests"       element={<MyRequests />} />
          <Route path="service-requests"  element={<ServiceRequests />} />
          <Route path="request-approvals" element={<RequestApprovals />} />
          <Route path="leave-balances"    element={<LeaveBalances />} />
          <Route path="requests-report"   element={<RequestsReport />} />
          <Route path="hr"                element={<HR />} />
          <Route path="hr/:id"            element={<EmployeeProfile />} />
        </Route>
      </Routes>
    </BrowserRouter>
  </AuthProvider>
);

export default App;
